import { useCallback, useRef } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { useSelector } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import jwtDecode from 'jwt-decode';
import _extends from '@babel/runtime/helpers/esm/extends';
import { pick, omit } from 'lodash';

import {
  PRIVACY_POLICY_LINK,
  RECAPTCHA_SITE_KEY,
  TERMS_AND_CONDITIONS_LINK,
} from 'app/constants/constants';
import { FIELD_NAME } from 'app/constants/form-fields';

import selectAppState from 'app/store/selector';
import { complexPasswordValidation, isChecked } from 'app/utils/validators';
import Analytics from 'app/utils/Analytics';
import Form from '@guestyci/foundation/Form';
import { FormProvider } from '@guestyci/foundation/enums';
import { Col, Row } from '@guestyci/foundation/esm/Layout';
import FormField from '@guestyci/foundation/FormField';
import Input from '@guestyci/foundation/Input';
import TextField from '@guestyci/foundation/TextField';
import Checkbox from '@guestyci/foundation/Checkbox';
import Dialog, {
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@guestyci/foundation/Dialog';
import { alphaNumeric, minLength, required } from '@guestyci/foundation/validators';
import RaisedButton from '@guestyci/foundation/RaisedButton';
import createStyles from '@guestyci/foundation/createStyles';
import useTranslations from '@guestyci/localize/useTranslations';
import useQueryParams from 'app/hooks/useQueryParams';

import registration from 'app/API/handlers/registration';
import track from 'app/utils/dio/registration-page';

import AppStyle, { mediaScreen } from 'app/Styles';
import classNames from 'classnames';
import PasswordHint from './PasswordHint';
import Translations from '../Translations';
import dioInstance from '../utils/dio/init';

const useStyles = createStyles(() =>
  _extends(AppStyle, {
    formGroup: {
      width: '100%',
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gridGap: '1.5rem',

      [mediaScreen.md]: {
        gridTemplateColumns: '1fr',
        gridGap: '1rem',
      },
    },
    passwordWithHint: {
      '&:focus-within .hint-paper': {
        display: 'block',
      },
    },
  }));

const jssLabel = {
  root: {
    display: 'block',
  },
};

function RegistrationForm({ onError, onSuccess }) {
  const classes = useStyles();
  const recaptcha = useRef(null);
  const queryParams = useQueryParams();
  const state = useSelector(selectAppState);

  const [firstNameLabel, lastNameLabel, passwordLabel, promotionCodeLabel] = useTranslations(
    Translations.step1.form.labels
  );
  const [
    firstNamePlaceholder,
    lastNamePlaceholder,
    passwordPlaceholder,
    promotionCodePlaceholder,
  ] = useTranslations(Translations.step1.form.placeholders);
  const [submitButtonLabel] = useTranslations(Translations.step1.form.buttons);

  const handleSubmitForm = useCallback(
    async (formValues) => {
      track.formPageFillData({
        ...formValues,
        isGoogleSignUp: state.isGoogleSignUp,
      });
      track.formPageClickNext(state.isGoogleSignUp);

      // Create Captcha token
      const token = await recaptcha.current.executeAsync();

      const { data } = await registration({
        token,
        ...pick(state, ['email', 'googleIdToken']),
        ...omit(formValues, [FIELD_NAME.TERMS_POLICY_AGREED]),
        ...Analytics.getDTO(),
      });

      if (data.errorCode) {
        recaptcha.current.reset();

        return onError(data);
      }

      dioInstance.updateIdentity({ userId: data?.temp });
      if (data.success) {
        return onSuccess(data);
      }
    },
    [state, onError, onSuccess]
  );
  const initialValues = {
    [FIELD_NAME.FIRST_NAME]: queryParams.get('firstname') || '',
    [FIELD_NAME.LAST_NAME]: queryParams.get('lastname') || '',
    [FIELD_NAME.PROMO_CODE]: queryParams.get('r') || '',
  };
  if (state.isGoogleSignUp && state.googleIdToken.includes('.')) {
    const jwtData = jwtDecode(state.googleIdToken);
    initialValues[FIELD_NAME.FIRST_NAME] = jwtData?.given_name;
    initialValues[FIELD_NAME.LAST_NAME] = jwtData?.family_name;
  }

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={handleSubmitForm}
      render={({ handleSubmit, submitError, form, values, submitting }) => {
        return (
          <Form
            react-final-form="true"
            onSubmit={handleSubmit}
            provider={FormProvider.Final}
            fieldInstance={Field}
          >
            <Col spacing={6} span={12}>
              <div className={classes.formGroup} id="registration-form-container">
                <FormField
                  label={firstNameLabel}
                  name={FIELD_NAME.FIRST_NAME}
                  required
                  validate={[minLength(2), required, alphaNumeric]}
                >
                  <Input
                    className={classes.marketingFormInput}
                    placeholder={firstNamePlaceholder}
                    id="registration-form-first-name"
                    autoComplete="given-name"
                  />
                </FormField>

                <FormField
                  label={lastNameLabel}
                  name={FIELD_NAME.LAST_NAME}
                  required
                  validate={[minLength(2), required, alphaNumeric]}
                >
                  <Input
                    className={classes.marketingFormInput}
                    placeholder={lastNamePlaceholder}
                    id="registration-form-last-name"
                    autoComplete="family-name"
                  />
                </FormField>

                {state.isRegularSignUp && (
                  <FormField
                    label={passwordLabel}
                    jss={jssLabel}
                    name={FIELD_NAME.PASSWORD}
                    required
                    validate={[complexPasswordValidation]}
                    className={classes.passwordWithHint}
                  >
                    <Input
                      className={classes.marketingFormInput}
                      type="password"
                      id="sign-up-password"
                      placeholder={passwordPlaceholder}
                      autoComplete="new-password"
                      suffix={<PasswordHint />}
                    />
                  </FormField>
                )}

                <FormField label={promotionCodeLabel} name={FIELD_NAME.PROMO_CODE}>
                  <Input
                    className={classes.marketingFormInput}
                    placeholder={promotionCodePlaceholder}
                    id="registration-form-promo-code"
                    withActions={false}
                  />
                </FormField>
              </div>

              <Row fullWidth wrap justify="between">
                <FormField
                  type="checkbox"
                  name={FIELD_NAME.TERMS_POLICY_AGREED}
                  validate={[isChecked]}
                >
                  {({ input, meta }) => {
                    const showError = !meta.active && meta.touched && meta.error;
                    const termsOnChange = (...args) => {
                      input.onChange(...args);
                      if (args[0]?.target?.checked) {
                        track.formPageApproveTerms();
                      }
                    };
                    return (
                      <Col id="registration-checkbox-terms-conditions-container">
                        <Checkbox
                          name={input.name}
                          checked={input.checked}
                          onChange={termsOnChange}
                          onFocus={input.onFocus}
                          onBlur={input.onBlur}
                          id="registration-checkbox-terms-conditions"
                        >
                          <span className="pr-1">I have read and accept the</span>
                          <a
                            href={PRIVACY_POLICY_LINK}
                            target="_blank"
                            rel="noreferrer"
                            id="registration-checkbox-privacy-policy-url"
                            className={classNames('underline', classes.darkLink)}
                          >
                            Terms of Service
                          </a>
                          <span className="px-1">and</span>
                          <a
                            href={TERMS_AND_CONDITIONS_LINK}
                            target="_blank"
                            rel="noreferrer"
                            id="registration-checkbox-terms-url"
                            className={classNames('underline', classes.darkLink)}
                          >
                            Privacy Policy
                          </a>
                          .
                        </Checkbox>
                        {showError && (
                          <TextField
                            className="ml-2"
                            id="registration-terms-conditions-error"
                            color="error"
                          >
                            {meta.error}
                          </TextField>
                        )}
                      </Col>
                    );
                  }}
                </FormField>
              </Row>

              <RaisedButton
                disabled={submitting}
                isLoading={submitting}
                id="registration-submit-button"
                className={[classes.marketingSuccessButton, 'w-fill max-width-200']}
                type="submit"
              >
                {submitButtonLabel}
              </RaisedButton>
            </Col>

            <ReCAPTCHA
              ref={recaptcha}
              id="registration-recaptcha"
              size="invisible"
              sitekey={RECAPTCHA_SITE_KEY}
            />

            <Dialog
              id="registration-dialog-container"
              open={Boolean(submitError)}
              onClose={() => form.reset(values)}
            >
              <DialogHeader>Registration failed</DialogHeader>
              <DialogContent>
                <TextField>{submitError}</TextField>
              </DialogContent>
              <DialogFooter>
                <RaisedButton onClick={() => form.reset(values)}> Ok </RaisedButton>
              </DialogFooter>
            </Dialog>
          </Form>
        );
      }}
    />
  );
}

export default RegistrationForm;
